import { EvidenceSubmission, SubmitterOutcome } from '@/backend/models/evidenceSubmission';
import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { useRequirementDocumentationStore } from '@/frontend/store/RequirementDocumentationStore';
import { I18n } from '@/i18n/I18n';
import { useState } from 'react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, CircleCheckBig, GitBranch, OctagonX, Upload } from 'lucide-react';
import { FileIcon } from '../files/FileIcon';
import { TermsAndConditionsNotice } from '../TermsAndConditionsNotice';

interface SubmitEvidenceButton {
  categories: RequirementEvidenceCategory[];
}

export function SubmitEvidenceButton({ categories }: SubmitEvidenceButton) {
  const submissionsByCategory = useRequirementDocumentationStore(useShallow((s) => s.submissionsByCategory));
  const evidenceItems = useRequirementDocumentationStore(useShallow((s) => s.evidenceItems));
  const updateSubmitterOutcome = useRequirementDocumentationStore((s) => s.updateSubmitterOutcome);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [selectedOutcomes, setSelectedOutcomes] = useState<Record<string, SubmitterOutcome>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoriesWithPending = categories
    .map((cat) => ({
      category: cat,
      pendingSubmissions: (submissionsByCategory[cat.id] ?? []).filter(
        (s) => s.submitterOutcome === SubmitterOutcome.PENDING
      ),
    }))
    .filter(({ pendingSubmissions }) => pendingSubmissions.length > 0);

  if(categoriesWithPending.length === 0) {
    return null;
  }

  const getFileName = (submission: EvidenceSubmission): string => {
    const item = evidenceItems.find((i) => i.id === submission.evidenceItemId);
    return item?.file?.fileName ?? '';
  };

  const handleSelectOutcome = (submissionId: string, outcome: SubmitterOutcome) => {
    setSelectedOutcomes((prev) => ({ ...prev, [submissionId]: outcome }));
  };

  const hasSelections = Object.keys(selectedOutcomes).length > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await updateSubmitterOutcome(selectedOutcomes);
      setShowSubmitModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onHideModal = () => {
    setShowSubmitModal(false);
    setSelectedOutcomes({});
  };

  return (
    <>
      <Button variant="primary" className="fusa-btn-medium-with-icon" onClick={() => setShowSubmitModal(true)}>
        <Upload size={16} />
        {I18n.get('submitEvidence')}
      </Button>
      <Modal show={showSubmitModal}>
        <Modal.Body>
          <Modal.Title className="pb-1">{I18n.get('submitEvidence')}</Modal.Title>
          <p className="text-muted mb-3" style={{ fontSize: '14px', color: '#706965' }}>
            {I18n.get('selectStatusForDocuments')}
          </p>

          {categoriesWithPending.map(({ category, pendingSubmissions }) => (
            <div key={category.id} className="mb-3">
              <label className="form-label-neutral">{category.name}</label>
              <div className="d-flex flex-column gap-2">
                {pendingSubmissions.map((submission) => (
                  <div key={submission.id} className={`list-card-item${!selectedOutcomes[submission.id] ? ' evidence-submission-row-muted' : ''}`}>
                    <FileIcon fileName={getFileName(submission)} />
                    <div className="list-card-item-info">
                      <span className="list-card-item-name">{submission.title}</span>
                      <span className="list-card-item-detail">{getFileName(submission)}</span>
                    </div>
                    <OutcomeDropdown
                      selectedOutcome={selectedOutcomes[submission.id] ?? SubmitterOutcome.PENDING}
                      onSelect={(outcome) => handleSelectOutcome(submission.id, outcome)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <TermsAndConditionsNotice description={I18n.get('submitEvidenceTermsAndConditions')} />
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-blue-light fusa-btn-medium" onClick={onHideModal} disabled={isSubmitting}>
            {I18n.get('cancel')}
          </Button>
          <Button className="fusa-btn-medium fusa-btn-primary" onClick={() => void handleSubmit()} disabled={isSubmitting || !hasSelections}>
            {isSubmitting ? I18n.get('saving') : `${I18n.get('submitEvidence')} (${Object.keys(selectedOutcomes).length})`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const SELECTED_OUTCOME_CLASSES = {
  [SubmitterOutcome.PASSED]: 'bg-success text-white',
  [SubmitterOutcome.FAILED]: 'bg-danger text-white',
  [SubmitterOutcome.DEVIATED]: 'bg-warning text-yellow-900',
  [SubmitterOutcome.PENDING]: 'bg-light text-neutral-800',
};

const OUTCOME_LABELS = {
  [SubmitterOutcome.PASSED]: I18n.get('passed'),
  [SubmitterOutcome.FAILED]: I18n.get('failed'),
  [SubmitterOutcome.DEVIATED]: I18n.get('deviated'),
  [SubmitterOutcome.PENDING]: I18n.get('submitAs'),
};

const OUTCOME_ICONS = {
  [SubmitterOutcome.PASSED]: <CircleCheckBig size={16} />,
  [SubmitterOutcome.FAILED]: <OctagonX size={16} />,
  [SubmitterOutcome.DEVIATED]: <GitBranch size={16} />,
  [SubmitterOutcome.PENDING]: null,
};
function OutcomeDropdown({
  onSelect,
  selectedOutcome,
}: {
  selectedOutcome: SubmitterOutcome;
  onSelect: (outcome: SubmitterOutcome) => void;
}) {

  return (
    <Dropdown>
      <Dropdown.Toggle as="button" className={`${SELECTED_OUTCOME_CLASSES[selectedOutcome]} btn-menu`} aria-label={I18n.get('submitAs')}>
        {OUTCOME_ICONS[selectedOutcome]}
        {OUTCOME_LABELS[selectedOutcome]}
        <ChevronDown size={16} />
      </Dropdown.Toggle>
      <Dropdown.Menu className="outcome-dropdown-menu">
        <Dropdown.Item
          className="dropdown-item-passing"
          onClick={() => onSelect(SubmitterOutcome.PASSED)}
        >
          <span className="d-flex align-items-center gap-2">
            {OUTCOME_ICONS[SubmitterOutcome.PASSED]}
            {I18n.get('outcomePassing')}
          </span>
        </Dropdown.Item>
        <Dropdown.Item
          className="dropdown-item-failing"
          onClick={() => onSelect(SubmitterOutcome.FAILED)}
        >
          <span className="d-flex align-items-center gap-2">
            {OUTCOME_ICONS[SubmitterOutcome.FAILED]}
            {I18n.get('outcomeFailing')}
          </span>
        </Dropdown.Item>
        <Dropdown.Item
          className="dropdown-item-deviated"
          onClick={() => onSelect(SubmitterOutcome.DEVIATED)}
        >
          <span className="d-flex align-items-center gap-2">
            {OUTCOME_ICONS[SubmitterOutcome.DEVIATED]}
            {I18n.get('deviated')}
          </span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

